import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/SiteLayout";
import { Toaster, toast } from "sonner";
import { Calendar } from "../components/ui/calendar";
import { sv } from "date-fns/locale";
import {
  Leaf,
  Scissors,
  Car,
  CheckCircle2,
  ArrowRight,
  Loader2,
  Repeat,
  Sun,
  Sunset,
  Sunrise,
  CalendarDays,
  Ruler,
  Info,
  X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pricing constants (mirror backend)
// Grass: tiered descending kr/kvm
// 0–100: 1.00, 101–500: 0.90, 501–1000: 0.70, >1000: 0.50; max 5000 kvm
const GRASS_TIERS = [
  { cap: 100, rate: 1.0 },
  { cap: 500, rate: 0.9 },
  { cap: 1000, rate: 0.7 },
  { cap: 5000, rate: 0.5 },
];
const GRASS_MAX_SQM = 5000;

const HEDGE_PRICES = {
  mini: 200,
  liten: 350,
  mellan: 650,
  stor: 1100,
  jatte: 1500,
};
const CAR_PRICE = 350;

const SUBSCRIPTION_WEEKS = 4;
const SUBSCRIPTION_DISCOUNT = 0.15;

// Latest date we can work
const MAX_WORK_DATE = new Date("2026-07-18T00:00:00");

const SERVICES = [
  {
    id: "grasklippning",
    label: "Gräsklippning och trimmning",
    desc: "Klippning + kanttrimning",
    icon: Leaf,
    pricingHint: "Från 1 kr/kvm",
  },
  {
    id: "hacktrimning",
    label: "Häcktrimning",
    desc: "Formklippning av häck",
    icon: Scissors,
    pricingHint: "Från 350 kr",
  },
  {
    id: "biltvatt",
    label: "Biltvätt",
    desc: "Handtvätt utvändigt",
    icon: Car,
    pricingHint: "350 kr",
  },
];

const HEDGE_SIZES = [
  {
    id: "mini",
    label: "Mini",
    range: "Under 2 löpmeter",
    example: "T.ex. en liten avskärmning vid grinden eller runt en planteringsruta",
    price: 200,
  },
  {
    id: "liten",
    label: "Liten",
    range: "2–5 löpmeter",
    example: "T.ex. avgränsning mot grannen eller runt en uteplats",
    price: 350,
  },
  {
    id: "mellan",
    label: "Mellan",
    range: "5–15 löpmeter",
    example: "T.ex. vanlig villahäck längs ena tomtgränsen",
    price: 650,
  },
  {
    id: "stor",
    label: "Stor",
    range: "15–25 löpmeter",
    example: "T.ex. längre infart eller två tomtgränser",
    price: 1100,
  },
  {
    id: "jatte",
    label: "Jätte",
    range: "Över 25 löpmeter",
    example: "T.ex. häck runt hela tomten",
    price: 1500,
  },
];

const TIME_SLOTS = [
  { id: "morgon", label: "Morgon", range: "08:00–12:00", icon: Sunrise },
  { id: "eftermiddag", label: "Eftermiddag", range: "12:00–16:00", icon: Sun },
  { id: "kvall", label: "Kväll", range: "16:00–20:00", icon: Sunset },
];

const DAY_NAMES = [
  "söndag",
  "måndag",
  "tisdag",
  "onsdag",
  "torsdag",
  "fredag",
  "lördag",
];

function toISODate(d) {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDateLong(d) {
  if (!d) return "";
  return `${DAY_NAMES[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
}

function formatShort(d) {
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

function calcGrassPrice(sqm) {
  if (!sqm || sqm <= 0) return 0;
  const remaining = Math.min(sqm, GRASS_MAX_SQM);
  let total = 0;
  let prev = 0;
  for (const { cap, rate } of GRASS_TIERS) {
    const slice = Math.min(remaining, cap) - prev;
    if (slice > 0) total += slice * rate;
    if (remaining <= cap) break;
    prev = cap;
  }
  return Math.round(total);
}

export default function BookingPage() {
  const [selected, setSelected] = useState([]);
  const [lawnSqm, setLawnSqm] = useState(200);
  const [hedgeSize, setHedgeSize] = useState(null);
  const [subscription, setSubscription] = useState(false);
  const [bookingDate, setBookingDate] = useState(null);
  const [timeSlot, setTimeSlot] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]); // slots already booked on selected date
  const [loadingSlots, setLoadingSlots] = useState(false);
  const navigate = useNavigate();

  // Scroll to top when entering the booking page
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const hasGrass = selected.includes("grasklippning");
  const hasHedge = selected.includes("hacktrimning");
  const hasCar = selected.includes("biltvatt");

  const grassPrice = useMemo(() => (hasGrass ? calcGrassPrice(lawnSqm) : 0), [hasGrass, lawnSqm]);
  const hedgePrice = useMemo(
    () => (hasHedge && hedgeSize ? HEDGE_PRICES[hedgeSize] : 0),
    [hasHedge, hedgeSize],
  );
  const carPrice = hasCar ? CAR_PRICE : 0;
  const visitPrice = grassPrice + hedgePrice + carPrice;

  const grossSubscription = visitPrice * SUBSCRIPTION_WEEKS;
  const subscriptionTotal = Math.round(grossSubscription * (1 - SUBSCRIPTION_DISCOUNT));
  const total = subscription ? subscriptionTotal : visitPrice;
  const savings = subscription ? grossSubscription - subscriptionTotal : 0;

  const toggleService = (id) => {
    setSelected((prev) => {
      const next = prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id];
      if (!next.includes("hacktrimning")) setHedgeSize(null);
      return next;
    });
  };

  // Calendar bounds
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // For subscription, last visit = start + 3 weeks must be <= MAX_WORK_DATE
  // so latest start = MAX_WORK_DATE - 21 days
  const latestStart = useMemo(() => {
    if (!subscription) return MAX_WORK_DATE;
    const d = new Date(MAX_WORK_DATE);
    d.setDate(d.getDate() - 7 * (SUBSCRIPTION_WEEKS - 1));
    return d;
  }, [subscription]);

  // Clear bookingDate when subscription toggle changes the latestStart (handler-driven, not effect-driven)
  const handleSubscriptionToggle = () => {
    setSubscription((v) => {
      const next = !v;
      // After toggling, if currently-selected date is beyond next latestStart, clear it
      const nextLatest = next
        ? (() => {
            const d = new Date(MAX_WORK_DATE);
            d.setDate(d.getDate() - 7 * (SUBSCRIPTION_WEEKS - 1));
            return d;
          })()
        : MAX_WORK_DATE;
      if (bookingDate && bookingDate > nextLatest) {
        setBookingDate(null);
      }
      return next;
    });
  };

  const disabledMatcher = (d) =>
    d < today || d > latestStart || d.getDay() === 0;

  const subscriptionDates = useMemo(() => {
    if (!subscription || !bookingDate) return [];
    return Array.from({ length: SUBSCRIPTION_WEEKS }, (_, i) => {
      const d = new Date(bookingDate);
      d.setDate(d.getDate() + i * 7);
      return d;
    });
  }, [subscription, bookingDate]);

  // Fetch booked slots for the selected booking date (or union of visit dates if subscription)
  useEffect(() => {
    let cancelled = false;
    async function loadAvailability() {
      if (!bookingDate) {
        setBookedSlots([]);
        return;
      }
      setLoadingSlots(true);
      try {
        const datesToCheck = subscription
          ? Array.from({ length: SUBSCRIPTION_WEEKS }, (_, i) => {
              const d = new Date(bookingDate);
              d.setDate(d.getDate() + i * 7);
              return toISODate(d);
            })
          : [toISODate(bookingDate)];

        const results = await Promise.all(
          datesToCheck.map((d) =>
            axios
              .get(`${API}/availability/${d}`)
              .then((r) => r.data?.booked_slots || [])
              .catch(() => []),
          ),
        );
        if (cancelled) return;
        const union = Array.from(new Set(results.flat()));
        setBookedSlots(union);
        // Clear time slot if currently chosen one is now booked
        if (timeSlot && union.includes(timeSlot)) {
          setTimeSlot(null);
        }
      } finally {
        if (!cancelled) setLoadingSlots(false);
      }
    }
    loadAvailability();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingDate, subscription]);

  const canSubmit =
    selected.length > 0 &&
    (!hasGrass || (lawnSqm && lawnSqm >= 10)) &&
    (!hasHedge || !!hedgeSize) &&
    !!bookingDate &&
    !!timeSlot &&
    form.name.trim() &&
    form.email.trim() &&
    form.phone.trim() &&
    form.address.trim() &&
    !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      if (selected.length === 0) toast.error("Välj minst en tjänst.");
      else if (hasGrass && (!lawnSqm || lawnSqm < 10))
        toast.error("Ange gräsmattans storlek (minst 10 kvm).");
      else if (hasHedge && !hedgeSize) toast.error("Välj häckens storlek.");
      else if (!bookingDate) toast.error("Välj ett datum.");
      else if (!timeSlot) toast.error("Välj ett tidsfönster.");
      else toast.error("Fyll i alla obligatoriska fält.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        services: selected,
        lawn_sqm: hasGrass ? Number(lawnSqm) : null,
        hedge_size: hasHedge ? hedgeSize : null,
        subscription,
        booking_date: toISODate(bookingDate),
        time_slot: timeSlot,
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        notes: form.notes,
      };
      const res = await axios.post(`${API}/bookings`, payload);
      toast.success("Bokningsförfrågan skickad!");
      navigate("/betalning", { state: { booking: res.data } });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Något gick fel. Försök igen eller ring oss på 076-1479408.";
      toast.error(typeof msg === "string" ? msg : "Ett fel inträffade.");
    } finally {
      setSubmitting(false);
    }
  };

  // Numbering: 1=Services, 2=Sizes (if any), then Date, then Details
  const hasSizes = hasGrass || hasHedge;
  let n = 1;
  const stepNums = {
    services: n++,
    sizes: hasSizes ? n++ : null,
    when: n++,
    details: n++,
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="booking-page">
      <Toaster position="top-center" richColors />
      <Header />

      <main className="pt-24 sm:pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <>
              <div className="text-center">
                <div className="inline-flex items-center gap-2 rounded-full bg-green-50 text-green-700 ring-1 ring-green-200 px-3 py-1 text-xs font-semibold tracking-wide">
                  Snabbt &amp; enkelt
                </div>
                <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
                  Boka &amp; Få Prisförslag
                </h1>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  Välj tjänster, ange storlek, välj tid — bekräftelse skickas
                  direkt på mail.
                </p>
                <p className="mt-2 text-xs text-slate-500">
                  Vi tar uppdrag t.o.m. {formatShort(MAX_WORK_DATE)}{" "}
                  {MAX_WORK_DATE.getFullYear()}.
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                className="mt-10 space-y-8"
                data-testid="booking-form"
              >
                {/* STEP 1 — Services + Subscription */}
                <StepCard
                  step={stepNums.services}
                  title="Välj tjänster"
                  subtitle="Välj en eller flera"
                >
                  <div className="grid gap-3 sm:grid-cols-3">
                    {SERVICES.map((s) => {
                      const Icon = s.icon;
                      const active = selected.includes(s.id);
                      return (
                        <button
                          type="button"
                          key={s.id}
                          onClick={() => toggleService(s.id)}
                          data-testid={`service-${s.id}`}
                          className={`text-left rounded-2xl p-5 transition-all border ${
                            active
                              ? "border-green-600 bg-green-50 ring-2 ring-green-600"
                              : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                          }`}
                          aria-pressed={active}
                        >
                          <div className="flex items-center justify-between">
                            <div
                              className={`inline-flex items-center justify-center w-10 h-10 rounded-xl ${
                                active
                                  ? "bg-green-600 text-white"
                                  : "bg-slate-100 text-slate-700"
                              }`}
                            >
                              <Icon size={20} />
                            </div>
                            {active && (
                              <CheckCircle2 size={20} className="text-green-600" />
                            )}
                          </div>
                          <div className="mt-4 font-semibold text-slate-900">
                            {s.label}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {s.desc}
                          </div>
                          <div className="mt-3 text-sm font-semibold text-green-700">
                            {s.pricingHint}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Subscription toggle */}
                  <div className="mt-6">
                    <button
                      type="button"
                      onClick={handleSubscriptionToggle}
                      data-testid="subscription-toggle"
                      aria-pressed={subscription}
                      className={`w-full text-left rounded-2xl p-5 sm:p-6 transition-all border relative overflow-hidden ${
                        subscription
                          ? "border-green-600 bg-gradient-to-br from-green-50 to-white ring-2 ring-green-600"
                          : "border-dashed border-slate-300 bg-white hover:border-green-400 hover:bg-green-50/40"
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div
                          className={`flex-shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-xl ${
                            subscription
                              ? "bg-green-600 text-white"
                              : "bg-slate-100 text-slate-700"
                          }`}
                        >
                          <Repeat size={22} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-bold text-slate-900 text-base sm:text-lg">
                              Abonnemang — samma dag varje vecka i 4 veckor
                            </div>
                            <span className="inline-flex items-center rounded-full bg-green-600 text-white px-2.5 py-0.5 text-xs font-bold">
                              −15%
                            </span>
                          </div>
                          <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                            Välj en startdag och tidsfönster — vi kommer på
                            samma veckodag i fyra veckor i rad. Spara{" "}
                            <span className="font-semibold text-green-700">
                              15%
                            </span>{" "}
                            jämfört med enskilda besök.
                          </p>
                        </div>
                        {subscription && (
                          <CheckCircle2 size={22} className="text-green-600 mt-1" />
                        )}
                      </div>
                    </button>
                  </div>
                </StepCard>

                {/* STEP — Sizes (conditional) */}
                {hasSizes && (
                  <StepCard
                    step={stepNums.sizes}
                    title="Storlek"
                    subtitle="Hjälper oss att räkna ut ett rättvist pris"
                  >
                    {hasGrass && (
                      <LawnSizeSelector
                        sqm={lawnSqm}
                        setSqm={setLawnSqm}
                        price={grassPrice}
                      />
                    )}

                    {hasGrass && hasHedge && <div className="h-6" />}

                    {hasHedge && (
                      <HedgeSizeSelector
                        value={hedgeSize}
                        onChange={setHedgeSize}
                      />
                    )}
                  </StepCard>
                )}

                {/* STEP — Date & Time */}
                <StepCard
                  step={stepNums.when}
                  title="Välj datum & tid"
                  subtitle={
                    subscription
                      ? `Startdag — sista besöket senast ${formatShort(MAX_WORK_DATE)}`
                      : `Måndag–lördag · 08:00–20:00 · senast ${formatShort(MAX_WORK_DATE)}`
                  }
                >
                  <div className="grid gap-6 md:grid-cols-2 items-start">
                    <div
                      className="rounded-2xl border border-slate-200 bg-white p-2 sm:p-3"
                      data-testid="booking-calendar"
                    >
                      <Calendar
                        mode="single"
                        selected={bookingDate}
                        onSelect={setBookingDate}
                        disabled={disabledMatcher}
                        locale={sv}
                        weekStartsOn={1}
                        fromDate={today}
                        toDate={latestStart}
                        defaultMonth={bookingDate || today}
                        className="mx-auto"
                      />
                    </div>

                    <div>
                      <div className="flex items-center gap-2 text-sm text-slate-700 font-medium">
                        <CalendarDays size={16} className="text-green-600" />
                        {bookingDate ? (
                          <span data-testid="selected-date">
                            {formatDateLong(bookingDate)}
                          </span>
                        ) : (
                          <span className="text-slate-500">
                            Inget datum valt
                          </span>
                        )}
                      </div>

                      <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                        Tidsfönster
                        {loadingSlots && (
                          <Loader2 size={12} className="animate-spin text-slate-400" />
                        )}
                      </div>
                      <div className="mt-2 grid gap-2.5" data-testid="time-slot-group">
                        {TIME_SLOTS.map((t) => {
                          const Icon = t.icon;
                          const active = timeSlot === t.id;
                          const isBooked =
                            !!bookingDate && bookedSlots.includes(t.id);
                          const disabled = isBooked;
                          return (
                            <button
                              type="button"
                              key={t.id}
                              onClick={() => !disabled && setTimeSlot(t.id)}
                              disabled={disabled}
                              data-testid={`time-slot-${t.id}`}
                              data-booked={isBooked ? "true" : "false"}
                              className={`flex items-center gap-3 rounded-xl p-3.5 text-left transition-all border ${
                                disabled
                                  ? "border-red-300 bg-red-50 text-red-700 cursor-not-allowed opacity-90"
                                  : active
                                    ? "border-green-600 bg-green-50 ring-2 ring-green-600"
                                    : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                              }`}
                              aria-pressed={active}
                              aria-disabled={disabled}
                            >
                              <div
                                className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${
                                  disabled
                                    ? "bg-red-100 text-red-600"
                                    : active
                                      ? "bg-green-600 text-white"
                                      : "bg-slate-100 text-slate-700"
                                }`}
                              >
                                <Icon size={18} />
                              </div>
                              <div className="flex-1">
                                <div
                                  className={`font-semibold text-sm ${
                                    disabled ? "text-red-700" : "text-slate-900"
                                  }`}
                                >
                                  {t.label}
                                </div>
                                <div
                                  className={`text-xs ${
                                    disabled ? "text-red-600" : "text-slate-500"
                                  }`}
                                >
                                  {disabled ? "Upptaget" : t.range}
                                </div>
                              </div>
                              {disabled ? (
                                <X size={18} className="text-red-500" />
                              ) : (
                                active && (
                                  <CheckCircle2 size={18} className="text-green-600" />
                                )
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {subscription && subscriptionDates.length > 0 && (
                        <div
                          className="mt-5 rounded-xl bg-white border border-green-200 p-4"
                          data-testid="subscription-preview"
                        >
                          <div className="text-xs font-semibold uppercase tracking-wider text-green-700">
                            Dina 4 abonnemangsbesök
                          </div>
                          <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
                            {subscriptionDates.map((d, i) => (
                              <li key={i} className="flex items-center gap-2">
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold">
                                  {i + 1}
                                </span>
                                <span>{formatDateLong(d)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {subscription && (
                        <div className="mt-4 flex items-start gap-2 text-xs text-slate-500">
                          <Info size={14} className="mt-0.5 flex-shrink-0" />
                          <span>
                            Vi tar inga uppdrag efter {formatShort(MAX_WORK_DATE)}.
                            Senast möjliga startdag för abonnemang:{" "}
                            <strong className="text-slate-700">
                              {formatShort(latestStart)}
                            </strong>
                            .
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </StepCard>

                {/* STEP — Details + Summary */}
                <StepCard
                  step={stepNums.details}
                  title="Dina uppgifter & prisförslag"
                  subtitle="Vi återkommer inom kort"
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Namn" required>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        data-testid="input-name"
                        className={inputClass}
                        placeholder="För- och efternamn"
                      />
                    </Field>
                    <Field label="E-post" required>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="input-email"
                        className={inputClass}
                        placeholder="namn@exempel.se"
                      />
                    </Field>
                    <Field label="Telefon" required>
                      <input
                        type="tel"
                        required
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        data-testid="input-phone"
                        className={inputClass}
                        placeholder="07X-XXX XX XX"
                      />
                    </Field>
                    <Field label="Adress" required>
                      <input
                        type="text"
                        required
                        value={form.address}
                        onChange={(e) => setForm({ ...form, address: e.target.value })}
                        data-testid="input-address"
                        className={inputClass}
                        placeholder="Gatuadress, ort"
                      />
                    </Field>
                  </div>
                  <Field label="Meddelande (valfritt)" className="mt-4">
                    <textarea
                      rows={3}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      data-testid="input-notes"
                      className={inputClass}
                      placeholder="Önskat datum, särskilda önskemål..."
                    />
                  </Field>

                  <PriceSummary
                    selected={selected}
                    lawnSqm={lawnSqm}
                    hedgeSize={hedgeSize}
                    subscription={subscription}
                    grassPrice={grassPrice}
                    hedgePrice={hedgePrice}
                    carPrice={carPrice}
                    visitPrice={visitPrice}
                    total={total}
                    savings={savings}
                    grossSubscription={grossSubscription}
                  />

                  <button
                    type="submit"
                    disabled={!canSubmit}
                    data-testid="submit-booking-btn"
                    className={`mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-semibold shadow-sm transition-all ${
                      canSubmit
                        ? "bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg"
                        : "bg-slate-200 text-slate-500 cursor-not-allowed"
                    }`}
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" /> Skickar...
                      </>
                    ) : (
                      <>
                        Skicka bokningsförfrågan <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                  <p className="mt-3 text-xs text-slate-500 text-center">
                    Genom att skicka godkänner du att vi kontaktar dig om din
                    bokning.
                  </p>
                </StepCard>
              </form>
            </>
        </div>
      </main>

      <Footer />
    </div>
  );
}

const inputClass =
  "block w-full rounded-xl border-0 py-3 px-4 text-slate-900 bg-white shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-green-600 text-sm";

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-sm font-medium text-slate-900 mb-1.5">
        {label} {required && <span className="text-green-700">*</span>}
      </span>
      {children}
    </label>
  );
}

function StepCard({ step, title, subtitle, children }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-600 text-white font-bold inline-flex items-center justify-center">
          {step}
        </div>
        <div className="flex-1">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
            {title}
          </h2>
          {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="mt-6">{children}</div>
    </section>
  );
}

function LawnSizeSelector({ sqm, setSqm, price }) {
  const presets = [100, 300, 500, 1000, 2500, 5000];
  const handleChange = (v) => {
    let n = Number(v);
    if (Number.isNaN(n) || n < 0) n = 0;
    if (n > GRASS_MAX_SQM) n = GRASS_MAX_SQM;
    setSqm(n);
  };
  // Show which tier the current sqm falls into
  const currentTier = (() => {
    if (sqm <= 100) return { label: "0–100 kvm", rate: "1,50 kr/kvm" };
    if (sqm <= 500) return { label: "101–500 kvm", rate: "0,90 kr/kvm" };
    if (sqm <= 1000) return { label: "501–1000 kvm", rate: "0,70 kr/kvm" };
    return { label: "1001+ kvm", rate: "0,50 kr/kvm" };
  })();

  return (
    <div data-testid="lawn-sqm-selector">
      <div className="flex items-center gap-2 mb-3">
        <Ruler size={16} className="text-green-600" />
        <h3 className="font-semibold text-slate-900">Gräsmattans storlek</h3>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        Ange ungefärlig yta i kvadratmeter. Ju större tomt, desto lägre
        kvm-pris.
      </p>

      <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 sm:p-5">
        <div className="flex items-end justify-between gap-4">
          <div className="flex items-baseline gap-2">
            <input
              type="number"
              min={10}
              max={GRASS_MAX_SQM}
              step={10}
              value={sqm}
              onChange={(e) => handleChange(e.target.value)}
              data-testid="lawn-sqm-input"
              className="w-28 text-2xl sm:text-3xl font-extrabold text-slate-900 bg-white rounded-xl border-0 ring-1 ring-inset ring-slate-300 px-3 py-2 focus:ring-2 focus:ring-green-600"
            />
            <span className="text-base font-semibold text-slate-700">kvm</span>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-green-700 font-semibold">
              Pris
            </div>
            <div
              className="text-2xl sm:text-3xl font-extrabold text-slate-900"
              data-testid="lawn-price"
            >
              {price} kr
            </div>
          </div>
        </div>

        <input
          type="range"
          min={10}
          max={GRASS_MAX_SQM}
          step={10}
          value={Math.min(sqm, GRASS_MAX_SQM)}
          onChange={(e) => handleChange(e.target.value)}
          data-testid="lawn-sqm-slider"
          className="mt-5 w-full accent-green-600"
        />
        <div className="mt-1 flex justify-between text-[11px] text-slate-500">
          <span>10 kvm</span>
          <span>1000 kvm</span>
          <span>5000 kvm</span>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {presets.map((p) => {
            const active = sqm === p;
            return (
              <button
                type="button"
                key={p}
                onClick={() => setSqm(p)}
                data-testid={`lawn-preset-${p}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                  active
                    ? "bg-green-600 text-white border-green-600"
                    : "bg-white text-slate-700 border-slate-300 hover:border-green-400"
                }`}
              >
                {p} kvm
              </button>
            );
          })}
        </div>

        {/* Tier ladder */}
        <div className="mt-5 rounded-xl bg-white border border-slate-200 p-3.5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Prisskala (fallande)
          </div>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
            {[
              { range: "0–100 kvm", rate: "1,50 kr/kvm" },
              { range: "101–500 kvm", rate: "0,90 kr/kvm" },
              { range: "501–1000 kvm", rate: "0,70 kr/kvm" },
              { range: "1001–5000 kvm", rate: "0,50 kr/kvm" },
            ].map((t) => {
              const active = currentTier.label.startsWith(t.range.split("–")[0]) ||
                (t.range === currentTier.label);
              return (
                <li
                  key={t.range}
                  className={`flex items-center justify-between rounded-md px-2 py-1 ${
                    active
                      ? "bg-green-50 text-green-800 font-semibold"
                      : "text-slate-600"
                  }`}
                >
                  <span>{t.range}</span>
                  <span className="font-semibold">{t.rate}</span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="mt-3 flex items-start gap-2 text-xs text-slate-500">
          <Info size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            Vald nivå:{" "}
            <strong className="text-slate-700">{currentTier.label}</strong> ·{" "}
            {currentTier.rate}. Större tomter får automatiskt lägre kvm-pris.
          </span>
        </div>
      </div>
    </div>
  );
}

function HedgeSizeSelector({ value, onChange }) {
  return (
    <div data-testid="hedge-size-selector">
      <div className="flex items-center gap-2 mb-3">
        <Scissors size={16} className="text-green-600" />
        <h3 className="font-semibold text-slate-900">Häckens storlek</h3>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        Mät ungefärlig längd på häcken (löpmeter). Är du osäker — välj utifrån
        exemplen nedan.
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="hedge-size-group">
        {HEDGE_SIZES.map((opt) => {
          const active = value === opt.id;
          return (
            <button
              type="button"
              key={opt.id}
              onClick={() => onChange(opt.id)}
              data-testid={`hedge-size-${opt.id}`}
              className={`rounded-2xl p-5 text-left transition-all border h-full ${
                active
                  ? "border-green-600 bg-green-50 ring-2 ring-green-600"
                  : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
              }`}
              aria-pressed={active}
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold text-slate-900">{opt.label}</div>
                {active && <CheckCircle2 size={18} className="text-green-600" />}
              </div>
              <div className="text-xs font-medium text-slate-700 mt-1">
                {opt.range}
              </div>
              <p className="text-xs text-slate-500 mt-2 leading-snug">
                {opt.example}
              </p>
              <div className="mt-4 text-sm font-bold text-green-700">
                {opt.price} kr
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PriceSummary({
  selected,
  lawnSqm,
  hedgeSize,
  subscription,
  grassPrice,
  hedgePrice,
  carPrice,
  visitPrice,
  total,
  savings,
  grossSubscription,
}) {
  const lines = selected.map((s) => {
    if (s === "grasklippning") {
      return {
        id: s,
        label: `Gräsklippning och trimmning — ${lawnSqm || 0} kvm`,
        price: grassPrice,
      };
    }
    if (s === "hacktrimning") {
      const hedgeLabels = {
        mini: "Mini (<2 lm)",
        liten: "Liten (2–5 lm)",
        mellan: "Mellan (5–15 lm)",
        stor: "Stor (15–25 lm)",
        jatte: "Jätte (>25 lm)",
      };
      return {
        id: s,
        label: `Häcktrimning${hedgeSize ? ` — ${hedgeLabels[hedgeSize]}` : " — välj storlek"}`,
        price: hedgePrice,
      };
    }
    if (s === "biltvatt") {
      return { id: s, label: "Biltvätt", price: carPrice };
    }
    return null;
  });

  return (
    <div
      className="mt-6 rounded-2xl border border-green-200 bg-green-50/70 p-5 sm:p-6"
      data-testid="price-summary"
    >
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-green-800 uppercase tracking-wider">
          Prisförslag
        </div>
        <div className="text-xs text-green-700">
          {subscription ? "4 besök · −15%" : "Uppskattat pris"}
        </div>
      </div>

      {lines.length === 0 ? (
        <div className="mt-3 text-sm text-slate-600">
          Välj minst en tjänst för att se prisförslag.
        </div>
      ) : (
        <>
          <ul className="mt-3 divide-y divide-green-200/60">
            {lines.map((l) => (
              <li
                key={l.id}
                className="flex items-center justify-between py-2 text-sm gap-3"
              >
                <span className="text-slate-800">{l.label}</span>
                <span className="font-semibold text-slate-900 whitespace-nowrap">
                  {l.price ? `${l.price} kr` : "—"}
                </span>
              </li>
            ))}
          </ul>

          {subscription && (
            <div className="mt-3 rounded-xl bg-white border border-green-200 p-3 text-sm">
              <div className="flex items-center justify-between text-slate-700">
                <span>Pris per besök</span>
                <span className="font-semibold">{visitPrice} kr</span>
              </div>
              <div className="flex items-center justify-between text-slate-700 mt-1">
                <span>4 besök (utan rabatt)</span>
                <span className="line-through text-slate-400">
                  {grossSubscription} kr
                </span>
              </div>
              <div className="flex items-center justify-between text-green-700 font-semibold mt-1">
                <span>Abonnemangsrabatt (15%)</span>
                <span data-testid="savings-amount">−{savings} kr</span>
              </div>
            </div>
          )}
        </>
      )}

      <div className="mt-4 pt-4 border-t border-green-200 flex items-end justify-between">
        <div className="text-sm text-slate-700 font-medium">
          {subscription ? "Totalt (4 besök)" : "Totalt"}
        </div>
        <div
          className="text-3xl font-extrabold text-slate-900"
          data-testid="total-price"
        >
          {total} kr
        </div>
      </div>
    </div>
  );
}
